import {
    inject,
    Injectable,
} from '@angular/core';

import {
    endsWith,
    isEmpty,
    startsWith,
    upperFirst,
} from 'lodash';

import {
    ActiveSaleService,
} from 'src/app/features/cashregister/services/active-sale.service';

import {
    ConfigurationManagerService,
} from 'src/app/core/services/configuration-manager/configuration-manager.service';

import {
    ConnectionService,
} from './connection.service';

import {
    ErrorsLoggerService,
} from './errors-logger.service';


/**
 * The audit log: what the user did, sent to the logging service. Audits are on when the shop preference says so, and
 * by default for French shops that have a RetailForce client.
 */
@Injectable({
    providedIn: 'root',
})
export class AuditService {
    private readonly configuration = inject(ConfigurationManagerService);
    private readonly errorsLogger = inject(ErrorsLoggerService);

    private started = false;

    /**
     * Starts listening to what the application already announces on its own streams: the connection going on and off and
     * the events of the active sale. The rest of the audit entries are sent by the code that does the thing, with `log`.
     * Safe to call more than once.
     */
    public init(): void {
        if (this.started) {
            return;
        }

        this.started = true;

        ConnectionService.connectionStatus$.subscribe((status) => {
            this.log(`application${upperFirst(status.online ? 'online' : 'offline')}`);
        });

        ActiveSaleService.activeSaleEvents$.subscribe((event) => {
            switch (event.event) {
                case 'sale-opened':
                    if (!isEmpty(event.data)) {
                        this.log('saleOpened', { openedSale: event.data });
                    }

                    break;
                case 'sale-closed':
                    if (!isEmpty(event.data)) {
                        this.log('saleClosed', { closedSale: event.data });
                    }

                    break;
                case 'sale-parked':
                    this.log('saleParked', { parkedSale: event.data });
                    break;
                case 'item-changed':
                    this.log('saleItemUpdated', event.data);
                    break;
                case 'item-removed':
                    this.log('saleItemRemoved', { removedSaleItem: event.data });
                    break;
            }
        });
    }

    /** The application finished starting: the version, a new login if there was one, and the training (demo, sandbox) shops. */
    public logStartup(user: { shop: { name: string } }, newLogin: boolean, appVersion: string): void {
        this.log('applicationStartup', { appVersion });

        if (newLogin) {
            this.log('newLoggedUser', { user });
        }

        if (startsWith(user.shop.name, 'demo_') || endsWith(user.shop.name, '_sandbox')) {
            this.log('trainingModeStart');
        }
    }

    public isEnabled(): boolean {
        const preference = this.configuration.getShopPreference('logging.enable_audits');

        if (preference !== null && preference !== undefined) {
            return !!preference;
        }

        return this.configuration.getShopCountry() === 'FR' && !!this.configuration.getSettingUserFirst('retailforce.client_id');
    }

    /**
     * Sends an audit entry (`type` without the `audit` prefix) when audits are enabled. The caller does not wait for it and
     * is never hurt by it: a failure to audit is swallowed.
     */
    public log(type: string, data?: object): void {
        try {
            if (this.isEnabled()) {
                this.errorsLogger.sendAudit(type, data).catch(() => undefined);
            }
        } catch {
            // Auditing must not break the operation being audited
        }
    }
}
